import type { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useQueryParams } from '@/hooks/useQueryParams';
import { serializeEventKey } from '../lib/eventInstance.lib';
import type { DecoratedEvent } from '../types';

export const EVENT_QUERY_PARAM = 'event';

// The ?event= URL param is the source of truth for which event is open (see
// useQueryEvent); view-only semantics derive from the page context
// in EditEventModalLoader. A recurring event additionally names the instance
// it was opened at, as `{eventId}:{instanceStartMillis}`.
export const useEditEventModal = () => {
  const { queryParams, setQueryParams, removeQueryParam } = useQueryParams();

  const open = useCallback(
    (event: DecoratedEvent, instanceStart?: DateTime) => {
      // Only recurring events are instance-scoped; the server derives a single
      // event's instance from the event itself and ignores a client-sent one.
      const eventKey = serializeEventKey(
        event.id,
        event.rrule ? instanceStart : undefined
      );
      // Compare the whole ref, not just the id — against an instance-scoped
      // param an id-only comparison never matches and pushes a duplicate
      // history entry every time the same instance is re-opened.
      if (queryParams.get(EVENT_QUERY_PARAM) !== eventKey) {
        setQueryParams({ [EVENT_QUERY_PARAM]: eventKey });
      }
    },
    [queryParams, setQueryParams]
  );

  const close = useCallback(() => {
    removeQueryParam(EVENT_QUERY_PARAM, { replace: true });
  }, [removeQueryParam]);

  return { open, close };
};
